import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.theamazinggrace.in";
const OG_IMAGE = `${SITE_URL}/catalogue/cover.jpg`;

const SEO_MAP = {
  "/": {
    title: "Hotel Linen & Hospitality Textile Supplier in India | Amazing Grace",
    description:
      "Amazing Grace is a hotel linen and hospitality textile manufacturer and supplier in India, providing bed sheets, towels, bedding and hospitality textiles to hotels, resorts, hospitals and institutions across India.",
    breadcrumb: [["Home", "/"]],
  },
  "/about": {
    title: "About Amazing Grace | Hotel Linen Manufacturer & Supplier in India",
    description:
      "Learn about Amazing Grace, a Coimbatore-based manufacturer and supplier of hotel linen, bed sheets, towels, bedding and hospitality textiles serving customers across India.",
    breadcrumb: [["Home", "/"], ["About Us", "/about"]],
  },
  "/products": {
    title: "Hotel Linen Products | Bed Sheets, Towels & Hospitality Textiles | Amazing Grace",
    description:
      "Explore hotel bed sheets, bed linen, towels, duvet covers, pillows, mattresses, mattress protectors, bedspreads, blankets and other hospitality textiles from Amazing Grace.",
    breadcrumb: [["Home", "/"], ["Products", "/products"]],
    itemList: true,
  },
  "/catalogue": {
    title: "Hotel Linen Product Catalogue | Bed Sheets, Towels & Hospitality Textiles",
    description:
      "View the Amazing Grace hotel linen catalogue featuring bed sheets, towels, duvet covers, pillows, mattresses, bedspreads and other hospitality textile products.",
    breadcrumb: [["Home", "/"], ["Product Catalogue", "/catalogue"]],
    itemList: true,
  },
  "/solutions": {
    title: "Hospitality Textile Solutions for Hotels & Resorts | Amazing Grace",
    description:
      "Hospitality textile solutions for hotels, resorts, hospitals, institutions and other businesses, including hotel bedding, towels and hospitality essentials from Amazing Grace.",
    breadcrumb: [["Home", "/"], ["Hospitality Solutions", "/solutions"]],
  },
  "/customers": {
    title: "Our Hospitality Customers | Amazing Grace",
    description:
      "Discover the hospitality businesses and customers associated with Amazing Grace and our hotel linen and hospitality textile supply capabilities.",
    breadcrumb: [["Home", "/"], ["Our Customers", "/customers"]],
  },
  "/contact": {
    title: "Contact Amazing Grace | Hotel Linen Supplier in India",
    description:
      "Contact Amazing Grace for hotel linen, bed sheets, towels, bedding and hospitality textile requirements. Based in Karamadai, Coimbatore, Tamil Nadu and serving customers across India.",
    breadcrumb: [["Home", "/"], ["Contact", "/contact"]],
  },
  "/quote": {
    title: "Request a Quote | Hotel Linen & Hospitality Textiles | Amazing Grace",
    description:
      "Request a quotation for hotel bed sheets, towels, duvet covers, pillows, mattresses and hospitality textiles. Amazing Grace supplies hotels, resorts and institutions across India.",
    breadcrumb: [["Home", "/"], ["Get a Quote", "/quote"]],
  },
};

const DEFAULT_SEO = SEO_MAP["/"];

const PRODUCT_ITEMS = [
  "Hotel Bed Sheets", "Hotel Bed Linen", "Bed Runners", "Duvets", "Duvet Covers",
  "Hotel Pillows", "Pillow Covers", "Mattresses", "Mattress Protectors",
  "Hotel Bath Towels", "Pool Towels", "Hand Towels", "Face Towels", "Bath Mats",
  "Bathrobes", "Table Napkins", "Cushions", "Cushion Covers", "Blankets", "Bedspreads",
];

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Amazing Grace",
  description:
    "Manufacturer and supplier of hotel linen, hospitality textiles and hotel essentials, serving hotels, resorts, hospitals and institutions across India.",
  url: SITE_URL,
  logo: `${SITE_URL}/assets/logo.png`,
  image: OG_IMAGE,
  email: "contactus.amazinggrace@gmail.com",
  telephone: "+91 76038 10376",
  address: {
    "@type": "PostalAddress",
    streetAddress: "26 Mookambikai Nagar, Teachers Colony, Karamadai",
    addressLocality: "Coimbatore",
    addressRegion: "Tamil Nadu",
    postalCode: "641104",
    addressCountry: "IN",
  },
  areaServed: [
    { "@type": "Country", name: "India" },
    { "@type": "State", name: "Tamil Nadu" },
    { "@type": "State", name: "Kerala" },
    { "@type": "State", name: "Karnataka" },
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91 76038 10376",
    contactType: "sales",
    availableLanguage: ["English", "Tamil"],
  },
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const key = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
    const seo = SEO_MAP[key] || DEFAULT_SEO;
    const url = SITE_URL + (SEO_MAP[key] ? key : "/");

    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("name", "robots", "index, follow");
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Amazing Grace");
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", OG_IMAGE);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    setJsonLd("ld-org", ORG_SCHEMA);
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: seo.breadcrumb.map(([name, path], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item: SITE_URL + path,
      })),
    });
    if (seo.itemList) {
      setJsonLd("ld-products", {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Hotel Linen & Hospitality Textile Products",
        itemListElement: PRODUCT_ITEMS.map((name, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name,
        })),
      });
    } else {
      const el = document.getElementById("ld-products");
      if (el) el.remove();
    }
  }, [pathname]);

  return null;
};

export default Seo;
